import { ArrayHelpers, FieldArray } from 'formik';
import React from 'react';
import { APITypes, Dynamo, MedicalDataTypes } from '../../../../primary-record-types';
import ResourceImageInput from './ResourceImageInput';
import { getIntl } from '../../../../utils/intl';
import { CreateImageValue, FormValueImage, UpdateImageValue } from './types';
import { generateRandomID } from '../../../../utils/generateRandomID';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import Divider from '../../../v2BaseComponents/Divider';
import { getSpacing } from '../../../../utils/spacing';
import Link from '../../../v2BaseComponents/Link';
import { useScrollRef } from '../../Scroll/hooks';

const intl = getIntl('images-field', {
    images: 'Add Image +',
    add: 'Add',
});

export const resourceImagesToFormValueImages = (resourceImages: Dynamo.ResourceImage[]): FormValueImage[] => {
    return resourceImages.map((resourceImage) => ({
        uniqueElementId: generateRandomID(20),
        description: resourceImage.description,
        imageValue: {
            imageChange: { type: 'not-modified' },
            resourceImageId: resourceImage.resourceImageId,
            type: 'update',
        },
    }));
};

export const formValueImagesToResourceImageUpdates = (
    formValueImages: FormValueImage[],
): APITypes.medicalDataService.ResourceImageUpdate[] => {
    return formValueImages.map<APITypes.medicalDataService.ResourceImageUpdate>((formValueImage) => {
        if (formValueImage.imageValue.type === 'create') {
            const create: APITypes.medicalDataService.ResourceImageCreate = {
                type: 'create',
                description: formValueImage.description,
                tempFileId: formValueImage.imageValue.tempFileId,
            };
            return create;
        } else {
            const update: APITypes.medicalDataService.ResourceImageUpdate = {
                type: 'update',
                description: formValueImage.description,
                resourceImageId: formValueImage.imageValue.resourceImageId,
                imageChange: formValueImage.imageValue.imageChange,
            };
            return update;
        }
    });
};

export type ImagesFieldOwnProps = {
    values: FormValueImage[];
    isDisabled: boolean;
    resourceId?: string;
    profileId: string;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    setFieldValue: (field: string, value: UpdateImageValue) => void;
};

export type ImagesFieldProps = ImagesFieldOwnProps;

const ImagesField: React.FC<ImagesFieldProps> = (props) => {
    const scrollRef = useScrollRef();
    return (
        <FieldArray name="images">
            {(arrayHelpers: ArrayHelpers<FormValueImage[]>) => (
                <Stack
                    vertical
                    space={contentStackSpacing}
                    fullWidth
                    style={{ marginTop: getSpacing(2.5), marginBottom: getSpacing(2.5) }}
                >
                    <Stack horizontal justify={'space-between'} align="center" fullWidth>
                        <Link
                            variant="bold-body-1"
                            linkColor="blue"
                            onPress={() => {
                                const newValue: CreateImageValue = {
                                    uniqueElementId: generateRandomID(20),
                                    description: '',
                                    imageValue: {
                                        type: 'create',
                                        // @ts-expect-error TS2322
                                        tempFileId: undefined,
                                    },
                                };
                                arrayHelpers.push(newValue);
                                setTimeout(() => {
                                    scrollRef?.current?.scrollToEnd({ animated: true });
                                }, 100);
                            }}
                            testID="add-image"
                        >
                            {intl('images')}
                        </Link>
                    </Stack>
                    {props.values.map((image, index) => (
                        <ResourceImageInput
                            key={image.uniqueElementId}
                            onClear={() => arrayHelpers.remove(index)}
                            value={image}
                            index={index}
                            isDisabled={props.isDisabled}
                            resourceId={props.resourceId}
                            profileId={props.profileId}
                            category={props.category}
                            setFieldValue={props.setFieldValue}
                        />
                    ))}
                    {props.values.length ? <Divider horizontal /> : null}
                </Stack>
            )}
        </FieldArray>
    );
};

export default React.memo(ImagesField);
